import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { loadMe } = useAuth();
  const [status, setStatus] = useState("checking");

  const sessionId = params.get("session_id");
  const paypalSubscriptionId = params.get("subscription_id");

  useEffect(() => {
    const paymentId = sessionId || paypalSubscriptionId;
    if (!paymentId) { setStatus("error"); return undefined; }

    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = sessionId
          ? await api.get(`/payments/status/${sessionId}`)
          : await api.get(`/payments/paypal/status/${paypalSubscriptionId}`);
        if (r.payment_status === "paid") {
          await loadMe();
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(r.payment_status)) {
          setStatus("error");
          return;
        }
      } catch { /* retry */ }
      if (tries++ < 10) timer = setTimeout(poll, 1800);
      else setStatus("timeout");
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [loadMe, sessionId, paypalSubscriptionId]);

  return (
    <div className="min-h-[70vh] grid place-items-center text-center">
      <div className="max-w-md">
        {status === "checking" && <><Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" /><p className="text-muted-foreground">{t("payment.confirming")}</p></>}
        {status === "paid" && <>
          <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-extrabold mb-2">{t("payment.success")}</h1>
          <p className="text-muted-foreground mb-6">{t("payment.successHint")}</p>
          <Button onClick={() => nav("/dashboard")} data-testid="payment-continue">{t("payment.goDashboard")}</Button>
        </>}
        {(status === "error" || status === "timeout") && <>
          <XCircle className="w-14 h-14 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold mb-2">{t("payment.failed")}</h1>
          <p className="text-muted-foreground mb-6">{t("payment.failedHint")}</p>
          <Button variant="outline" onClick={() => nav("/billing")}>{t("payment.backBilling")}</Button>
        </>}
      </div>
    </div>
  );
}
